import type { z, ZodTypeAny } from "zod"

import { ZodEffects, ZodObject, ZodRawShape, ZodType } from "zod"

/**
 * Extracts the inner type of a zod effect object recursively
 */
export function innerType<T extends ZodTypeAny>(zodSchema: ZodEffects<T> | T): T {
  if (zodSchema instanceof ZodEffects) {
    return innerType(zodSchema.innerType())
  }

  return zodSchema
}

export type ZodSchemaKeys<Schema extends z.Schema<any, any>> = keyof z.input<Schema>

type PartialSafeParseResult<Schema extends z.AnyZodObject> =
  | {
      type: "success"
      data: z.output<Schema>
    }
  | {
      type: "error"
      invalidData: Record<string, unknown>
    }
  | {
      type: "partial"
      data: Partial<z.output<Schema>>
      invalidData: Record<string, unknown>
    }

/**
 * Parses a zod schema and returns a partial result with valid parsed and invalid data if some fields are invalid
 * @param zodSchema
 * @param data
 * @param ignoreValidationKeys keys that can be passed through without validation from the source object
 */
export function partialSafeParse<Schema extends z.AnyZodObject>(zodSchema: Schema, data: unknown, ignoreValidationKeys: ZodSchemaKeys<Schema>[]): PartialSafeParseResult<Schema> {
  const result = zodSchema.safeParse(data)

  if (result.success) return { type: "success", data: result.data }

  const { fieldErrors } = result.error.flatten() ?? {}

  const keysWithInvalidData = Object.keys(fieldErrors ?? {})

  const validInputObject = omitKeysFromObject(data as Record<string, unknown>, keysWithInvalidData)
  // Omit all keys that are invalid from zod schema and parse the valid field values
  const validData = zodSchema.omit(Object.fromEntries(keysWithInvalidData.map(key => [key, true]))).parse(validInputObject)
  // @ts-ignore
  const passthroughData = pickKeysFromObject(data as Record<string, unknown>, ignoreValidationKeys)

  const invalidData = pickKeysFromObject(data as Record<string, unknown>, arrayWithout(keysWithInvalidData, ignoreValidationKeys as string[]))

  const resultData = { ...passthroughData, ...validData }

  if (Object.keys(resultData).length === 0) return { type: "error", invalidData }

  return { type: "partial", data: resultData, invalidData }
}

export function arrayWithout(array: string[], without: string[]) {
  return array.filter(item => !without.includes(item))
}

/**
 * Omits keys from object
 */
export const omitKeysFromObject = <Obj extends Record<string, unknown>>(obj: Obj, keys: (keyof Obj | string)[]) => {
  if (keys.length === 0) return obj
  const entries = Object.entries(obj) as [keyof Obj, unknown][]
  return Object.fromEntries(entries.filter(([key]) => !keys.includes(key)))
}

/**
 * Pick keys from object
 */
export const pickKeysFromObject = <Obj extends Record<string, unknown>>(obj: Obj, keys: (keyof Obj | string)[]) => {
  if (keys.length === 0) return {}
  const entries = Object.entries(obj) as [keyof Obj, unknown][]
  return Object.fromEntries(entries.filter(([key]) => keys.includes(key)))
}
